import { z } from 'zod';

import {
  type FunctionDefinition,
  type RouteDefinition,
  SlideScansGetResponse,
  ModelsConfig,
  shared,
  tokenRequestBodySchema,
  tokenResponseBodySchema,
  ModelsConfigResponse,
} from '@tig-dpqa-cloud/contract-backend-common';

import { VerifyMultipleRequest, VerifyRequest, VerifyRequestQueryParams, VerifySlideScanResponse } from './schemas/slide-scan-verify';
import { DataplaneRegisterRequestSchema, DataplaneRegisterResponseSchema } from './schemas/dataplane-register';
import { FilterInfoGetResponse } from './schemas/scanner-info-get';
import { SlideScansListResponse, SlideScanListQueryParams, dashboardParams } from './schemas/slide-scans-list';
import { UserContextGetResponseSchema } from './schemas/user-context-get';
import { UserWithPermissions, UserCreationBody, UserIdSchema, UserPermissionsSchema, UsersGetResponse } from './schemas/users-get';
import { UsersUpdateResponse } from './schemas/users-update';
import { AthenaCredentials } from './schemas/athena-credentials';

import { type PermissionType, Permissions } from './permissions';
import { DashboardLiveGetResponse } from './schemas/dashboard-live-get';
import { DataplaneConfigCredentials } from './schemas/dataplane-config-credentials';
import { dataplaneUpgradeApproveBody } from './schemas/dataplane-upgrade-approve';
import { TenantPatchBody } from './schemas/tenant';
import { UserAgreementPatchBody } from './schemas/user-agreements-patch';

export const authorizer: FunctionDefinition = {
  infra: {
    entrypointRelativeToFunctionsFolder: './authorizers/src/frontend/index.ts',
    dbAccess: 'BYPASS',
    needsEgress: true,
  },
};

export const asyncFunctions = {
  asyncMarkAnonGeneric: {
    infra: { entrypointRelativeToFunctionsFolder: './slide-scans/src/mark-anon-generic/index.ts', dbAccess: 'ISOLATED' },
  },
  asyncRemoteTestHelpers: {
    infra: { entrypointRelativeToFunctionsFolder: './remote-test-utils/src/index.ts', dbAccess: 'BYPASS' },
  },
  asyncCleanupTenantSlideScans: {
    infra: { entrypointRelativeToFunctionsFolder: './events/src/cleanup-cloud-tenant-slide-scans-async/index.ts', dbAccess: 'BYPASS' },
  },
  asyncCleanupBucket: {
    infra: { entrypointRelativeToFunctionsFolder: './events/src/cleanup-bucket-async/index.ts', dbAccess: 'BYPASS' },
  },
  asyncCleanupCloud: {
    infra: { entrypointRelativeToFunctionsFolder: './events/src/cleanup-cloud-async/index.ts', dbAccess: 'BYPASS' },
  },
  asyncGenerateContours: {
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/generate-metadata-async/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  asyncGetTenants: {
    infra: {
      entrypointRelativeToFunctionsFolder: './tenants/src/tenants-async/index.ts',
      dbAccess: 'BYPASS',
    },
  },
} as const satisfies { [key: string]: FunctionDefinition };

/**
 * ! BE AWARE
 * When creating the IAM policy, there is no way to have an "internal" wildcard, meaning that a * on the resource, immediately includes all postfixes.
 * We have to be aware of this, and how the paths will convert to a resource in the authorizer.
 * From the first route parameter, everything will be wildcards:
 *  * Pick your paths smart and according to the permissions. Make sure that no arns would be constructed that are very permissive
 *  * The HTTP method is part of the ARN, so two paths that will be mapped to the same resource path, will still have different arns.
 * For clarity's sake, the function that will convert the paths for the authorizer is also defined below. See examples in the test file.
 */
export const routes = {
  getUserContext: {
    path: '/user/context',
    method: 'get',
    successResponse: {
      description: 'The context for the authenticate user',
      jsonSchema: UserContextGetResponseSchema,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './users/src/context-get/frontend/index.ts',
      dbAccess: 'ISOLATED', // we also update the user name(s) from CIAM here, so we need write access
    },
  },
  getUsers: {
    path: '/users',
    method: 'get',
    successResponse: {
      description: 'List of users for current tenant',
      jsonSchema: UsersGetResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './users/src/users-get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  postUsers: {
    path: '/users',
    method: 'post',
    request: {
      body: UserCreationBody,
    },
    successResponse: {
      code: '201',
      description: 'The created user',
      jsonSchema: UserWithPermissions,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './users/src/users-post/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  patchUsers: {
    path: '/users/{id}',
    method: 'patch',
    request: {
      body: UserPermissionsSchema,
    },
    successResponse: {
      description: 'The update user',
      jsonSchema: UsersUpdateResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './users/src/users-update/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  deleteUser: {
    path: '/users/{id}',
    method: 'delete',
    request: {
      params: z.object({
        id: UserIdSchema,
      }),
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './users/src/users-delete/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  patchTenant: {
    method: 'patch',
    path: '/tenant',
    infra: {
      entrypointRelativeToFunctionsFolder: './tenants/src/tenant-patch/index.ts',
      dbAccess: 'ISOLATED',
    },
    request: {
      body: TenantPatchBody,
    },
  },
  getSlideScanList: {
    path: '/slide-scans',
    method: 'get',
    description:
      'NOTE: the query fields need to be under the `sfp` fields itself, this is due to lambda limitations that we are working around for now',
    request: {
      query: SlideScanListQueryParams,
    },
    successResponse: {
      description: 'The requested slide scans',
      jsonSchema: SlideScansListResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/list-get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  getSlideScan: {
    path: '/slide-scans/{id}',
    method: 'get',
    request: {
      params: z.object({
        id: shared.SlideScanId,
      }),
    },
    successResponse: {
      description: 'A singe slide scan.',
      jsonSchema: SlideScansGetResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  putVerifyMultiple: {
    path: '/slide-scans/verify',
    method: 'put',
    request: {
      body: VerifyMultipleRequest,
    },
    successResponse: {
      description: 'Updated slide scans.',
      jsonSchema: z.array(VerifySlideScanResponse),
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/verify-multiple-put/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  putVerify: {
    path: '/slide-scans/{id}/verify',
    method: 'put',
    request: {
      params: z.object({
        id: shared.SlideScanId,
      }),
      query: VerifyRequestQueryParams,
      body: VerifyRequest,
    },
    successResponse: {
      description: 'Updated slide scan.',
      jsonSchema: VerifySlideScanResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/verify-put/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  getFilterInfo: {
    path: '/filter-info',
    method: 'get',
    successResponse: {
      description: 'The filter information',
      jsonSchema: FilterInfoGetResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/filter-info-get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  postToken: {
    path: '/token',
    method: 'post',
    request: {
      body: tokenRequestBodySchema,
    },
    unauthenticated: true,
    successResponse: {
      description: 'The requested tokens',
      jsonSchema: tokenResponseBodySchema,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './authentication/src/token-post/index.ts',
      dbAccess: 'NOACCESS',
      needsEgress: true,
    },
  },
  postRegisterDataplane: {
    path: '/dataplane/register',
    method: 'post',
    request: {
      body: DataplaneRegisterRequestSchema,
    },
    successResponse: {
      description: 'The certificates for the newly registered dataplane.',
      jsonSchema: DataplaneRegisterResponseSchema,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/register-post/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  headDataplaneOnboarded: {
    path: '/dataplane/{id}',
    method: 'head',
    request: {
      params: z.object({
        id: shared.DataplaneId,
      }),
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/onboarded-head/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  getEventCredentials: {
    path: '/events/credentials',
    method: 'get',
    successResponse: {
      description: 'The credentials for making athena requests',
      jsonSchema: AthenaCredentials,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './events/src/athena-creds-get/index.ts',
      dbAccess: 'NOACCESS',
    },
  },
  getLiveDashboardData: {
    path: '/dashboard/live',
    method: 'get',
    request: {
      query: dashboardParams,
    },
    successResponse: {
      description: 'The live dashboard data',
      jsonSchema: DashboardLiveGetResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/dashboard-live-get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  putDemoData: {
    path: '/demo/data',
    method: 'put',
    description: 'Removes all data & uploads demo data for the current tenant.',
    infra: {
      entrypointRelativeToFunctionsFolder: './demo/src/demo-data-put/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  getCredentialsForConfig: {
    method: 'get',
    path: '/dataplane/config/credentials',
    successResponse: {
      description: 'The credentials to update the dataplane configuration',
      jsonSchema: DataplaneConfigCredentials,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/config-creds-get/index.ts',
      dbAccess: 'NOACCESS',
    },
  },
  postUpgradeApprove: {
    method: 'post',
    path: '/dataplane/upgrade/approve',
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/upgrade-approve-post/index.ts',
      dbAccess: 'ISOLATED_READONLY',
      needsEgress: true,
    },
    request: {
      body: dataplaneUpgradeApproveBody,
    },
  },
  patchUserAgreements: {
    method: 'patch',
    path: '/user/agreements',
    request: {
      body: UserAgreementPatchBody,
    },
    successResponse: {
      description: 'The updated user',
      jsonSchema: UsersUpdateResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './users/src/agreements-patch/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  getModelsConfiguration: {
    method: 'get',
    path: '/models/configuration',
    successResponse: {
      description: 'The models configuration for the current tenant',
      jsonSchema: ModelsConfigResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './models/src/models-configuration-get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  getModelsConfigurationFromVersion: {
    method: 'get',
    path: '/models/configuration/{version}',
    successResponse: {
      description: 'The models configuration for the current tenant',
      jsonSchema: ModelsConfigResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './models/src/models-configuration-from-version-get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  postModelsConfiguration: {
    method: 'post',
    path: '/models/configuration/{version}',
    request: {
      body: ModelsConfig,
    },
    successResponse: {
      description: 'The version of the updated models configuration ',
      jsonSchema: ModelsConfigResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './models/src/models-configuration-post/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
} as const satisfies { [key: string]: RouteDefinition };

export type Functions = keyof typeof routes | keyof typeof asyncFunctions | 'authorizer';

export const permissionsToRoutesMap = {
  [Permissions.enum.DEFAULT]: [routes['getUserContext'], routes['postToken'], routes['patchUserAgreements']],
  [Permissions.enum.DASHBOARD_READ]: [routes['getEventCredentials'], routes['getLiveDashboardData'], routes['getFilterInfo']],
  [Permissions.enum.SLIDE_SCANS_READ]: [
    routes['getSlideScanList'],
    routes['getSlideScan'],
    routes['getFilterInfo'],
    routes['getModelsConfiguration'],
    routes['getModelsConfigurationFromVersion'],
  ],
  [Permissions.enum.SLIDE_SCANS_WRITE]: [routes['putVerify'], routes['putVerifyMultiple']],
  [Permissions.enum.SETUP_WRITE]: [
    routes['patchTenant'],
    routes['postRegisterDataplane'],
    routes['headDataplaneOnboarded'],
    routes['getCredentialsForConfig'],
    routes['postUpgradeApprove'],
    routes['postModelsConfiguration'],
  ],
  [Permissions.enum.SETUP_READ]: [
    routes['getModelsConfiguration'],
  ],
  [Permissions.enum.USER_MANAGEMENT_WRITE]: [
    routes['getUsers'],
    routes['patchUsers'],
    routes['postUsers'],
    routes['deleteUser'],
  ],
  [Permissions.enum.DATAPLANE_CONFIGURATION_WRITE]: [],
  [Permissions.enum.DEMO]: [routes['putDemoData']],
} as const satisfies Record<PermissionType, RouteDefinition[]>;

export const convertToArnResourcePostfix = ({ path, method }: RouteDefinition) =>
  `${method.toUpperCase()}${path.replace(/\{.*$/, '*')}`;
